class LazyVideo extends HTMLElement {
  connectedCallback() {
    this.poster = this.querySelector('.lazy-video__poster');
    this.intersected = false;
    this.currentVariant = null;
    this.video = null;
    this.observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.mql?.removeEventListener('change', this.onMediaChange);
    this.mql = null;
  }

  isDesktop() {
    if (!this.mql) {
      const bp = parseInt(this.getAttribute('breakpoint') || '768', 10);
      this.mql = window.matchMedia(`(min-width: ${bp}px)`);
      this.onMediaChange = this.onMediaChange || (() => {
        if (!this.intersected) return;
        const wanted = this.wantedVariant();
        if (this.currentVariant === wanted) return;
        if (!this.querySelector(`template[data-variant="${wanted}"]`)) return;
        this.removeVideo();
        this.poster?.classList.remove('is-hidden');
        this.mount(wanted);
      });
      this.mql.addEventListener('change', this.onMediaChange);
    }
    return this.mql.matches;
  }

  wantedVariant() {
    return this.isDesktop() ? 'desktop' : 'mobile';
  }

  pickTemplate(variant) {
    const fallback = variant === 'desktop' ? 'mobile' : 'desktop';
    for (const v of [variant, fallback]) {
      const tpl = this.querySelector(`template[data-variant="${v}"]`);
      if (tpl && tpl.content.querySelector('video')) return tpl;
    }
    return null;
  }

  observe() {
    const rootMargin = this.getAttribute('root-margin') || '200px';
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.intersected = true;
          this.mountWhenReady(this.wantedVariant());
          this.observer?.disconnect();
          this.observer = null;
          break;
        }
      }
    }, { rootMargin });
    this.observer.observe(this);
  }

  // A hero video sits inside the viewport from the very first frame, so the
  // intersection observer fires immediately and its download (several MB at
  // preload="auto") ends up competing with the page's critical resources.
  // The poster is already painted eagerly — it is the LCP element — so the
  // video itself can wait until the page has finished loading. Videos further
  // down the page are unaffected and still mount as soon as they scroll near.
  mountWhenReady(variant) {
    if (!this.hasAttribute('data-kv-banner') || document.readyState === 'complete') {
      this.mount(variant);
      return;
    }

    window.addEventListener(
      'load',
      () => {
        const run = () => this.mount(variant);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, { timeout: 2000 });
        } else {
          setTimeout(run, 200);
        }
      },
      { once: true }
    );
  }

  mount(variant) {
    const tpl = this.pickTemplate(variant);
    if (!tpl) return;
    const fragment = tpl.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!video) return;
    const isKvBanner = this.hasAttribute('data-kv-banner');
    video.muted = true;
    video.playsInline = true;
    video.classList.add('lazy-video__video');
    const reveal = () => this.poster?.classList.add('is-hidden');

    if (isKvBanner) {
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    } else {
      video.addEventListener('loadeddata', reveal, { once: true });
    }

    video.addEventListener('playing', reveal, { once: true });
    this.video = video;
    this.currentVariant = variant;
    this.appendChild(fragment);

    const playVideo = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    if (isKvBanner) {
      video.addEventListener('loadedmetadata', playVideo, { once: true });
      video.addEventListener('canplay', playVideo, { once: true });
      video.load();
    }

    if (video.hasAttribute('autoplay')) {
      playVideo();
    }
  }

  removeVideo() {
    this.video?.remove();
    this.video = null;
    this.currentVariant = null;
  }
}
if (!customElements.get('lazy-video')) customElements.define('lazy-video', LazyVideo);

class VideoIntersection extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._video = null;
    this._isPlaying = false;
  }

  connectedCallback() {
    this._video = this.querySelector('video');
    if (!this._video) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const visibleHeight = entry.intersectionRect.height;

        if (visibleHeight >= 100 && !this._isPlaying) {
          this._video.play().catch(() => {});
          this._isPlaying = true;
        } else if (visibleHeight < 100 && this._isPlaying) {
          this._video.pause();
          this._isPlaying = false;
        }
      },
      {
        root: null,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    this._observer.observe(this._video);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
if (!customElements.get('video-intersection')) {
  customElements.define('video-intersection', VideoIntersection);
}

class VideoIntersectionInsert extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._isPlaying = false;
    this._loaded = false;
    this._playedOnce = false;
  }

  connectedCallback() {
    const videoHtml = this.dataset.src;
    if (!videoHtml) return;
    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const visibleHeight = entry.intersectionRect.height;
        if (this.hasAttribute('once') && this._playedOnce) return;
        if (visibleHeight >= 100 && !this._loaded) {
          const videoDiv = document.createElement('div');
          videoDiv.innerHTML = videoHtml;
          this._video = videoDiv.querySelector('video');
          this.querySelectorAll('img').forEach((b) => (b.style.visibility = 'hidden'));
          this.appendChild(this._video);
          this._video.addEventListener('ended', () => {
            if (this.hasAttribute('once')) {
              this._playedOnce = true;
              this._observer?.disconnect();
              this._video.pause();
            }
          });
          this._video.play().catch(() => {});
          this._loaded = true;
          this._isPlaying = true;
        }
        if (visibleHeight >= 100 && !this._isPlaying && !this._playedOnce) {
          this._video.play().catch(() => {});
          this._isPlaying = true;
        } else if (visibleHeight < 100 && this._isPlaying && !this.hasAttribute('once')) {
          this._video.pause();
          this._isPlaying = false;
        }
      },
      {
        root: null,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
if (!customElements.get('video-intersection-insert')) {
  customElements.define('video-intersection-insert', VideoIntersectionInsert);
}
